import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useCreateFakeThread } from '../hooks/useCreateFakeThread';
import { useInvalidateThread } from '../hooks/useInvalidateThread';
import { useRefreshThreads } from '../hooks/useRefreshThreads';
import { useRemoveThread } from '../hooks/useRemoveThread';
import { useThreadMessages } from '../hooks/useThreadMessages';
import { useThreads } from '../hooks/useThreads';
import { assistantResourceId } from '../lib/resourceId';
import { resolveThreadTitle } from '../lib/threadTitle';
import { AGENTS, type AgentNames, type Component, type MessageOrigin, type ResourceId, type UserTextMessage } from '../types';

// Types
export interface ThreadSelectOption {
  value: string;
  label: string;
  createdAt: string;
}

export type AIAssistantPendingInput = {
  id: string;
  input: string;
  /** Claimed by a submitter; survives remounts so the same prompt cannot be sent twice. */
  claimed?: boolean;
  /** Last submission failed; retry must be explicitly requested before another send. */
  failed?: boolean;
  /** Already uploaded and tracked by the shared composer; its thread exists. */
  message?: UserTextMessage;
  hidden?: boolean;
  /** How the prompt was produced, forwarded to the AI API when the pending input is sent. */
  origin?: MessageOrigin;
  /** Analytics `source` of the surface that parked the prompt (`home`, `news card`). */
  source?: string;
  /** Id of the clicked suggestion card; without it the backend cannot attach the card's fact to the answer. */
  suggestionId?: string;
};

export type AIAssistantSidepanelLayout = 'drawer' | 'inline' | null;
export type AIAssistantSidepanelContext = {
  agentName: AgentNames;
  component: Component;
  resourceId: ResourceId;
  extraBody?: Record<string, unknown>;
};

interface AIAssistantState {
  isExpanded: boolean;
  newThreadId: string;
  isAISidepanelOpen: boolean;
  aiSidepanelWidth: number;
  aiSidepanelLayout: AIAssistantSidepanelLayout;
  sidepanelContext: AIAssistantSidepanelContext | null;
  pendingInput: AIAssistantPendingInput | null;
  sidepanelOriginPath: string;
  /** O convite de WhatsApp vive no AppLayout e é modal: a introdução da /ai espera ele sair de cena. */
  isUserInviteModalOpen: boolean;
  setUserInviteModalOpen: (isUserInviteModalOpen: boolean) => void;
  /** Conversa cujo turno o chat da /ai está processando; a sidebar de threads vive fora do `useChat`. */
  respondingThreadId: string | null;
  setRespondingThreadId: (respondingThreadId: string | null) => void;
  /** Dev-only model override (Mastra format, e.g. "openrouter/deepseek/deepseek-v4-flash"). `null` = backend default. */
  selectedModel: string | null;
  setSelectedModel: (model: string | null) => void;
  openAISidepanel: () => void;
  setPendingInput: (input: string, origin?: MessageOrigin, source?: string, suggestionId?: string) => void;
  setPendingMessage: (message: UserTextMessage, conversationId: string, source: string) => void;
  setIsExpanded: (isExpanded: boolean) => void;
  setNewThreadId: (id: string) => void;
  resetNewThreadId: () => void;
  setAISidepanelWidth: (width: number) => void;
  setAISidepanelLayout: (layout: AIAssistantSidepanelLayout) => void;
  toggleAISidepanel: () => void;
  closeAISidepanel: () => void;
  openAISidepanelWithInput: (input: string, context?: AIAssistantSidepanelContext, origin?: MessageOrigin) => void;
  openAISidepanelWithHiddenInput: (input: string, context?: AIAssistantSidepanelContext, origin?: MessageOrigin) => void;
  clearPendingInput: () => void;
  /** Marks the pending input in flight without removing its retry payload. */
  claimPendingInput: () => AIAssistantPendingInput | null;
  completePendingInput: (id: string) => void;
  restorePendingInput: (id: string) => void;
  retryPendingInput: (id: string) => void;
  /** Takes the pending input and clears it in the same tick. */
  consumePendingInput: () => AIAssistantPendingInput | null;
}

const DEFAULT_AI_SIDEPANEL_WIDTH = 420;
const getCurrentPathname = () => (typeof window === 'undefined' ? '' : window.location.pathname);

// Store
const useStore = create<AIAssistantState>()(
  persist(
    (set, get) => ({
      isExpanded: false,
      newThreadId: crypto.randomUUID(),
      isAISidepanelOpen: false,
      aiSidepanelWidth: DEFAULT_AI_SIDEPANEL_WIDTH,
      aiSidepanelLayout: null,
      sidepanelContext: null,
      pendingInput: null,
      sidepanelOriginPath: '',
      isUserInviteModalOpen: false,
      respondingThreadId: null,
      selectedModel: null,
      setUserInviteModalOpen: (isUserInviteModalOpen) => set({ isUserInviteModalOpen }),
      setRespondingThreadId: (respondingThreadId) => set({ respondingThreadId }),
      setSelectedModel: (selectedModel) => set({ selectedModel }),
      setIsExpanded: (isExpanded) => set({ isExpanded }),
      setNewThreadId: (newThreadId) => set({ newThreadId }),
      resetNewThreadId: () => set({ newThreadId: crypto.randomUUID() }),
      setAISidepanelWidth: (aiSidepanelWidth) => set({ aiSidepanelWidth }),
      setAISidepanelLayout: (aiSidepanelLayout) => set({ aiSidepanelLayout }),
      toggleAISidepanel: () =>
        set((state) => ({
          isAISidepanelOpen: !state.isAISidepanelOpen,
          sidepanelOriginPath: state.isAISidepanelOpen ? state.sidepanelOriginPath : getCurrentPathname(),
          sidepanelContext: state.isAISidepanelOpen ? null : state.sidepanelContext,
        })),
      closeAISidepanel: () => set({ isAISidepanelOpen: false, sidepanelContext: null }),
      openAISidepanel: () => set({ isAISidepanelOpen: true, sidepanelContext: null, sidepanelOriginPath: getCurrentPathname() }),
      setPendingInput: (input, origin, source, suggestionId) =>
        set({ pendingInput: { id: crypto.randomUUID(), input, origin, source, suggestionId }, sidepanelOriginPath: getCurrentPathname() }),
      setPendingMessage: (message, conversationId, source) =>
        set({
          newThreadId: conversationId,
          pendingInput: {
            id: message.id,
            input: message.parts
              .filter((part) => part.type === 'text')
              .map((part) => part.text)
              .join('\n'),
            message,
            origin: message.metadata?.origin,
            source,
          },
          sidepanelOriginPath: getCurrentPathname(),
        }),
      openAISidepanelWithInput: (input, sidepanelContext, origin) =>
        set({
          isAISidepanelOpen: true,
          sidepanelContext: sidepanelContext ?? null,
          pendingInput: { id: crypto.randomUUID(), input, origin },
          sidepanelOriginPath: getCurrentPathname(),
        }),
      // Callers are contextual "Analisar com a IA" buttons, hence the `feature_action` default origin.
      openAISidepanelWithHiddenInput: (input, sidepanelContext, origin = 'feature_action') =>
        set({
          isAISidepanelOpen: true,
          sidepanelContext: sidepanelContext ?? null,
          pendingInput: { id: crypto.randomUUID(), input, hidden: true, origin },
          sidepanelOriginPath: getCurrentPathname(),
        }),
      clearPendingInput: () => set({ pendingInput: null }),
      claimPendingInput: () => {
        const { pendingInput } = get();
        if (!pendingInput || pendingInput.claimed || pendingInput.failed) return null;
        set({ pendingInput: { ...pendingInput, claimed: true } });
        return pendingInput;
      },
      completePendingInput: (id) =>
        set((state) => {
          if (state.pendingInput?.id !== id) return state;
          return { pendingInput: null };
        }),
      restorePendingInput: (id) =>
        set((state) => {
          if (state.pendingInput?.id !== id) return state;
          return { pendingInput: { ...state.pendingInput, claimed: false, failed: true } };
        }),
      retryPendingInput: (id) =>
        set((state) => {
          if (state.pendingInput?.id !== id) return state;
          return { pendingInput: { ...state.pendingInput, failed: false } };
        }),
      consumePendingInput: () => {
        const { pendingInput } = get();
        if (pendingInput) set({ pendingInput: null });
        return pendingInput;
      },
    }),
    {
      name: 'ai-assistant-sidepanel',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ aiSidepanelWidth: state.aiSidepanelWidth, aiSidepanelLayout: state.aiSidepanelLayout, selectedModel: state.selectedModel }),
    }
  )
);

/** Raw Zustand store — use only outside React (e.g. transporters) via `useAIAssistantStore.getState()`. In components use the `useAIAssistant` hook. */
export const useAIAssistantStore = useStore;

// Constants
const AGENT_NAME = AGENTS.agentFleetManager;
const AI_ROUTE = '/ai';

// Hook
export const useAIAssistant = () => {
  const {
    isExpanded,
    newThreadId,
    isAISidepanelOpen,
    aiSidepanelWidth,
    aiSidepanelLayout,
    sidepanelContext,
    setIsExpanded,
    setNewThreadId,
    resetNewThreadId,
    setAISidepanelWidth,
    setAISidepanelLayout,
    toggleAISidepanel,
    closeAISidepanel,
    pendingInput,
    openAISidepanel,
    setPendingInput,
    openAISidepanelWithInput,
    openAISidepanelWithHiddenInput,
    clearPendingInput,
    claimPendingInput,
    completePendingInput,
    restorePendingInput,
    retryPendingInput,
    consumePendingInput,
    sidepanelOriginPath,
    selectedModel,
    setSelectedModel,
  } = useStore();

  const { getUser, getSession } = useUser();
  const user = getUser();
  const session = getSession();
  const [threadId, setThreadId] = useQueryState('threadId', parseAsString.withDefault(''));
  const location = useLocation();
  const isAIRoute = location.pathname.startsWith(AI_ROUTE);

  const agentName = sidepanelContext?.agentName ?? AGENT_NAME;
  /*
   * Esta linha é a original, de antes do prefixo `assistant_` existir (#10940 o introduziu no chat
   * e não passou por aqui), então a LISTA apontava para outro balde: o do painel flutuante. Duas
   * consequências, e a segunda é a grave:
   *
   * 1. As conversas do próprio assistente nunca apareciam na lista dele — toda sessão em /ai
   *    parecia uma conversa nova, para sempre.
   * 2. Clicar numa conversa da lista mostrava as mensagens (buscadas por threadId), mas a resposta
   *    seguinte ia com o resourceId do assistente e um thread de outro dono: o guarda do `ai`
   *    (`isThreadOwnedByRequest`, AI-474) derruba o thread e a rota abre um novo. A conversa na
   *    tela virava órfã e o assistente respondia sem memória nenhuma dela.
   */
  const resourceId = sidepanelContext?.resourceId ?? assistantResourceId(user?.id, session?.fleet?.id);

  // Only fetch the conversation list when the assistant is actually visible —
  // the sidepanel is open (floating assistant) or we're on the /ai page.
  // Otherwise the always-mounted layout consumers (AIChatFloatingButton,
  // AIAssistantSidepanel) would fetch threads on every dashboard load.
  const { data: threads, isLoading: isLoadingThreads } = useThreads({
    agentName,
    resourceId,
    options: { enabled: isAISidepanelOpen || isAIRoute },
  });

  const {
    data: thread,
    isFetching: isFetchingThread,
    isError: isThreadError,
    refetch: refetchThread,
  } = useThreadMessages({
    agentName,
    threadId: threadId || undefined,
  });

  useEffect(() => {
    if (isThreadError && threadId) {
      setThreadId('');
    }
  }, [isThreadError, threadId, setThreadId]);

  const { mutateAsync: invalidateThread } = useInvalidateThread();
  const { mutateAsync: refetchThreads } = useRefreshThreads();
  const { mutateAsync: createFakeThread } = useCreateFakeThread();
  const { mutateAsync: removeThread } = useRemoveThread({
    agentName,
    resourceId,
  });

  const threadsArray = threads?.threads || [];
  const threadsOptions: ThreadSelectOption[] = threadsArray.map((t: any) => ({
    value: t.id,
    label: resolveThreadTitle(t.id, t.title),
    createdAt: t.createdAt,
  }));

  return {
    threadId,
    threads: threadsArray,
    thread,
    threadSelected: threadsArray.find((t: any) => t.id === threadId),
    threadsOptions,
    isLoadingThreads,
    isFetchingThread,
    isExpanded,
    newThreadId,
    isAISidepanelOpen,
    aiSidepanelWidth,
    aiSidepanelLayout,
    sidepanelContext,
    setThreadId,
    setNewThreadId,
    setIsExpanded,
    resetNewThreadId,
    setAISidepanelWidth,
    setAISidepanelLayout,
    toggleAISidepanel,
    closeAISidepanel,
    pendingInput,
    openAISidepanel,
    setPendingInput,
    openAISidepanelWithInput,
    openAISidepanelWithHiddenInput,
    clearPendingInput,
    claimPendingInput,
    completePendingInput,
    restorePendingInput,
    retryPendingInput,
    consumePendingInput,
    sidepanelOriginPath,
    refetchThread,
    refetchThreads,
    removeThread,
    createFakeThread,
    invalidateThread,
    selectedModel,
    setSelectedModel,
  };
};
